"use client";

import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { Check, CheckCircle2 } from "lucide-react";
import Link from "next/link";

export function HowItWorks() {
  const sectionRef = useRef(null);
  const lineRef = useRef(null);
  const [circleTop, setCircleTop] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !lineRef.current) return;

      const section = sectionRef.current;
      const headerHeight = 90;

      const sectionRect = section.getBoundingClientRect();

      // The circle should start moving only once the section's top
      // touches the bottom edge of the header (not the top of the viewport).
      // scrolled = how many px the section has moved past the header
      const scrolled = -(sectionRect.top - headerHeight);
      const scrollableDistance =
        sectionRect.height - (window.innerHeight - headerHeight);
      const progress = Math.min(Math.max(scrolled / scrollableDistance, 0), 1);

      const lineHeight = lineRef.current.offsetHeight;
      setCircleTop(progress * lineHeight);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // init

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="bg-[#11110d]" ref={sectionRef}>
      <div className="relative mx-auto max-w-6xl px-4 pt-28 pb-18 md:px-0">
        {/* Vertical Line */}
        <div className="absolute top-0 bottom-0 left-1/2 z-1 hidden lg:block">
          <div ref={lineRef} className="relative h-full w-px bg-neutral-100/50">
            {/* Tracking Circle */}
            <div
              className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 transition-[top] duration-75 ease-linear"
              style={{ top: `${circleTop}px` }}
            >
              {/* Outer glow ring */}
              <div
                className="absolute inset-0 size-5 -translate-x-1/4 -translate-y-1/4 animate-ping rounded-full bg-[#ffffff]/30"
                style={{ animationDuration: "2s" }}
              />
              {/* Inner dot */}
              <div className="size-3 rounded-full bg-[#ffffff]" />
            </div>
          </div>
        </div>

        <div className="flex flex-col items-start gap-18 lg:flex-row lg:gap-48">
          {/* LEFT SIDE - STICKY */}
          <div className="top-40 z-10 mt-8 self-start lg:sticky">
            <p className="text-base font-medium tracking-widest text-white">
              How it works
            </p>
            <p className="mt-5 text-3xl text-white lg:text-6xl">
              Launch effortlessly.
            </p>
            <p className="mt-1 bg-[linear-gradient(12deg,#ff9966_20%,#ff5e62_72%)] bg-clip-text text-3xl text-transparent lg:text-6xl">
              Scale endlessly.
            </p>
            <p className="mt-8 max-w-md text-xl text-white/90">
              We guide you through setup, import your data, and help your team
              get comfortable quickly.
            </p>
            <Button
              className="bg-fe-primary hover:ring-fe-primary/50 hover:fe-primary mt-10 h-11 font-medium hover:ring-4"
              asChild
            >
              <Link href="/signup">See how it works</Link>
            </Button>
          </div>

          {/* RIGHT SIDE - SCROLL CONTENT */}
          <div className="flex-1 space-y-15 text-white lg:space-y-30">
            <div className="flex max-w-full flex-col gap-4">
              <h4 className="text-2xl">Booking &amp; Scheduling</h4>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/landing_page/how-1.png"
                className="rounded-2xl"
                alt=""
              />
              <div className="space-y-2">
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Schedule and check out clients from one screen.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Fill openings automatically with the Intelligent Waitlist.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Block out time on the calendar.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Create repeating appointments.
                </p>
              </div>
            </div>
            <div className="flex max-w-full flex-col gap-4">
              <h4 className="text-2xl">Payments &amp; Invoicing</h4>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/landing_page/how-2.png"
                className="rounded-2xl"
                alt=""
              />
              <div className="space-y-2">
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Let clients pay from their own phone .
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Take deposits or full payments upfront.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Keep cards on file for faster checkout.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Allow clients to pay with their account balance.
                </p>
              </div>
            </div>
            <div className="flex max-w-full flex-col gap-4">
              <h4 className="text-2xl">Online Booking</h4>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/landing_page/how-3.png"
                className="rounded-2xl"
                alt=""
              />
              <div className="space-y-2">
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Embed online booking directly on your website and
                  google,instagram,facebook platform.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Allow clients to customize their appointments.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Require acceptance of your cancellation policy.
                </p>
                <p className="flex items-center gap-3">
                  <Check className="size-4.5" strokeWidth={1.2} />
                  Require a credit card for booking.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
